import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import type {
    BatchConfirmFileUploadData,
    BatchConfirmFileUploadBody,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/batch-confirm-file-upload'
import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IApiResponse } from '@/utils/api/api-types'
import { startTransition } from 'react'
import { track, TrackType } from '@/utils/tracking/track'
import { DirectoryImplementationType } from '@/features/drive/entities/directory/directory-context'
import { IBrandBaseParams, useApiToolbox } from '@/utils/api/use-api-toolbox'

type IBatchConfirmPayload = BatchConfirmFileUploadBody

export type IBatchConfirmNodeFileUploadMutationParams = IBatchConfirmPayload & IBrandBaseParams

type IBatchConfirmNodeFileUploadMutationPayload = Omit<
    IBatchConfirmNodeFileUploadMutationParams,
    'organizationId' | 'brandSlug'
>

export class BatchConfirmNodeFileUploadMutation {
    static async call(api: IApi, { organizationId, brandSlug, ...params }: IBatchConfirmNodeFileUploadMutationParams) {
        return api
            .post<
                BatchConfirmFileUploadData,
                IBatchConfirmNodeFileUploadMutationPayload
            >(`/organization/${organizationId}/brands/${brandSlug}/nodes/batch-confirm-file-upload`, params)
            .then((res) => res?.data)
    }
}

export function useBatchConfirmNodeFileUploadMutation(
    implementation: DirectoryImplementationType | null,
    options?: {
        infiniteNodeContentQueryKey?: QueryKey
        invalidateQueries?: boolean
    }
) {
    const { api, apiParams, qc } = useApiToolbox()

    const invalidateQueries = useBatchInvalidateQueriesOnConfirmNodeFileUpload()

    return useApiMutation<BatchConfirmFileUploadData, IBatchConfirmPayload>({
        mutationFn: (payload) => {
            return BatchConfirmNodeFileUploadMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            }).then(async (nodes) => {
                if (nodes && nodes.length > 0) {
                    startTransition(() => {
                        if (options?.infiniteNodeContentQueryKey) {
                            qc.setQueryData<InfiniteData<IApiResponse<INode[]>>>(
                                options.infiniteNodeContentQueryKey,
                                (prevData): InfiniteData<IApiResponse<INode[]>> | undefined => {
                                    if (!prevData) return prevData

                                    const existingUuids = new Set(
                                        prevData.pages.flatMap((page) => page.data.map((node) => node.uuid))
                                    )
                                    const newNodes = nodes.filter((n) => !existingUuids.has(n.uuid))
                                    if (newNodes.length === 0) return prevData

                                    return {
                                        ...prevData,
                                        // @ts-ignore
                                        pages: prevData.pages.map((prevPage, i) => {
                                            if (i !== 0) return prevPage
                                            return {
                                                ...prevPage,
                                                data: [...newNodes, ...prevPage.data],
                                            }
                                        }),
                                    }
                                }
                            )
                        }
                    })
                }

                if (options?.invalidateQueries === false) return nodes

                await invalidateQueries()

                return nodes
            })
        },
        onSuccess(data) {
            if (!data) return
            for (const node of data) {
                track(TrackType.FILE_UPLOADED, {
                    name: node.name,
                    path: node.path,
                    implementation: implementation,
                    file_type: node.file && node.file.type ? node.file.type : null,
                })
            }
        },
    })
}

export function useBatchInvalidateQueriesOnConfirmNodeFileUpload() {
    const qc = useQueryClient()

    return () => {
        return qc.invalidateQueries({
            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
        })
    }
}
